import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	createEditGameSettingsMutation,
	EditGameSettingsMutationParams,
	GameSettingWithGame,
	Setting,
} from 'shared/api';
import {
	createDynamicFormModel,
	createModalFormModel,
	DynamicForm,
	DynamicRule,
	FieldPayload,
	ModalFormModel,
	notify,
} from 'shared/lib';

export type EditGameSettingModel = ModalFormModel<GameSettingWithGame> & {
	editGameSettingsMutation: Mutation<EditGameSettingsMutationParams, null, JsonApiRequestError>;
	dynamicFormModel: DynamicForm;
	$pending: Store<boolean>;
};

export const createEditGameSettingModel = (): EditGameSettingModel => {
	const editGameSettingsMutation = createEditGameSettingsMutation();

	const model = createModalFormModel<GameSettingWithGame>({ fields: {} });

	const dynamicFormModel = createDynamicFormModel();

	const { $pending } = editGameSettingsMutation;

	chain(model.form.submitted, dynamicFormModel.submit);

	sample({
		clock: model.modalButtonClicked,
		fn: (setting) =>
			setting.settings.map((s) => {
				const rules: DynamicRule[] = [];

				if (s.type === 'array') {
					rules.push({
						validate: (value) =>
							Array.isArray(value) &&
							value.every((num, i, arr) => {
								if (i === 0) return true;

								return num > arr[i - 1];
							}),
						name: 'array-increasing',
					} satisfies DynamicRule);

					rules.push({
						validate: (value) => Array.isArray(value) && value.every((num) => num > 0),
						name: 'array-greater-zero',
					} satisfies DynamicRule);
				}

				return {
					key: s.code,
					type: s.type,
					value: s.value,
					rules,
				};
			}) as FieldPayload[],
		target: dynamicFormModel.addFields,
	});

	sample({
		clock: dynamicFormModel.submitted,
		source: {
			fields: dynamicFormModel.$fieldsArray,
			setting: model.$modalData,
		},
		filter: ({ setting }) => setting !== null,
		fn: ({ fields, setting }) => ({
			gameId: setting!.game.id,
			accountId: setting!.account.id,
			currency: setting!.currency,
			settings: fields.map((field) => ({
				code: field.key,
				type: field.type,
				value: field.value,
			})) as Setting[],
		}),
		target: editGameSettingsMutation.start,
	});

	chain(editGameSettingsMutation.finished.success, model.switcher.disable);

	notify(editGameSettingsMutation.finished.success, 'edit-game-setting.success');

	sample({
		clock: model.modalClosed,
		target: [editGameSettingsMutation.reset, dynamicFormModel.removeAllFields],
	});

	return {
		editGameSettingsMutation,
		dynamicFormModel,
		$pending,
		...model,
	};
};
